import React from 'react';
import { AutoTrackForm } from '../../../../components/form/form';
import { AutoComplete, Flex, Form, Modal, SelectProps } from 'antd';
import { AutoTrackButton } from '../../../../components/button';
import styled from 'styled-components';
import { useNotification } from '../../../../hooks/useNotification';
import { useDispatch } from 'react-redux';
import { setLoading } from '../../../../redux/slices/appInfo';
import { createCar, getCarsPaging } from '../../../../api/setting/cars';
import { Direction, SearchQueryParams } from '../../../../interface/paging';
import { removeUndefinedAttribute } from '../../../../utils';
import debouce from 'lodash/debounce';
import { useAddReceipts } from '../..';
import { AddLicensePlate } from '../../../components/add-license-plate';
import { CameraOCR } from '../../../components/camera-ocr';
import { ScannerType } from '../scanner-overlay';
import { SectionHeader } from '../SectionHeader';

const PlateInputWrapper = styled.div`
    display: flex;
    align-items: center;
    width: 100%;
    min-height: 48px;
    padding: 4px 4px 4px 0;
    border: 1px solid var(--tc-gray-g-200);
    border-radius: 10px;
    background: #fff;
    transition:
        border-color 0.2s,
        box-shadow 0.2s;

    &:hover {
        border-color: #7357ff;
    }

    &:focus-within {
        border-color: #7357ff;
        box-shadow: var(--shadow-pressed-primary);
    }

    .ant-select {
        flex: 1;
        min-width: 0;
    }

    .ant-select-selector {
        border: none !important;
        box-shadow: none !important;
        background: transparent !important;
        min-height: 40px !important;
        padding-inline: 11px 4px !important;
        align-items: center !important;
    }

    .ant-select-selection-search-input {
        height: 38px !important;
    }

    .ant-select-clear,
    .ant-select-arrow {
        display: none;
    }
`;

const UppercaseInput = styled(AutoComplete)`
    width: 100%;

    input {
        text-transform: uppercase;
    }
`;

const CarIcon = () => (
    <svg
        width="20"
        height="20"
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden
    >
        <path
            d="M18.92 6.01C18.72 5.42 18.16 5 17.5 5H15V3H9V5H6.5C5.84 5 5.28 5.42 5.08 6.01L3 12V20C3 20.55 3.45 21 4 21H5C5.55 21 6 20.55 6 20V19H18V20C18 20.55 18.45 21 19 21H20C20.55 21 21 20.55 21 20V12L18.92 6.01ZM6.5 16C5.67 16 5 15.33 5 14.5C5 13.67 5.67 13 6.5 13C7.33 13 8 13.67 8 14.5C8 15.33 7.33 16 6.5 16ZM17.5 16C16.67 16 16 15.33 16 14.5C16 13.67 16.67 13 17.5 13C18.33 13 19 13.67 19 14.5C19 15.33 18.33 16 17.5 16ZM5 11L6.5 6.5H17.5L19 11H5Z"
            fill="#7357FF"
        />
    </svg>
);

export const LicensePlateForm: React.FC = () => {
    const {
        carList,
        setCarList,
        setCarSelected,
        addLicensePlateForm,
        selectLicensePlateForm,
    } = useAddReceipts();
    const { showSuccess, showError } = useNotification();
    const dispatch = useDispatch();
    const [showNewCar, setShowNewCar] = React.useState(false);
    const Title: React.FC<Readonly<{ title?: string }>> = props => (
        <Flex align="center" justify="space-between">
            {props.title}
        </Flex>
    );
    const [licensePlate, setLicensePlate] = React.useState<string>('');

    const renderItem = (title: string) => ({
        value: title,
        label: <Flex align="center">{title}</Flex>,
    });

    const [carsOptions, setCarsOptions] = React.useState<
        SelectProps<object>['options']
    >([]);

    const onSubmit = React.useCallback(async () => {
        try {
            await selectLicensePlateForm.validateFields();
            const values = selectLicensePlateForm.getFieldsValue();
            const carSelection = carList.find(
                car => car.licensePlate === values.cardId
            );
            if (carSelection) {
                setCarSelected(carSelection);
            }
        } catch (error) {
            // Handle error
        }
    }, [selectLicensePlateForm, carList]);

    const onSaveNewCar = React.useCallback(async () => {
        try {
            await addLicensePlateForm.validateFields();
            const values = addLicensePlateForm.getFieldsValue();
            const payload = {
                ...values,
                year: values.year.format('YYYY'),
            };
            dispatch(setLoading(true));
            const result = await createCar(payload);
            if (result.status === 201) {
                showSuccess('Save Car success', 'Car has been created');
                setShowNewCar(false);
                setCarList(prev => [...prev, result.data]);
            }
        } catch (error) {
            showError('Save Car failed', 'Car has not been created');
        } finally {
            dispatch(setLoading(false));
        }
    }, [dispatch, addLicensePlateForm, showSuccess]);

    const loadData = async (val: string | undefined) => {
        try {
            const queryParams: SearchQueryParams = {
                page: 1,
                pageSize: 10,
                searchWord: val,
                sortBy: Direction.ASC,
                searchColum: 'LicensePlate',
            };
            const newPayload = removeUndefinedAttribute(queryParams);
            const response = await getCarsPaging(newPayload);
            if (response.status === 200) {
                const data = response.data?.data ?? [];
                setCarList(data);
                const options = data.map(item => ({
                    label: <Title title={item.model} />,
                    options: [renderItem(item.licensePlate)],
                }));
                setCarsOptions(options);
            }
        } catch (error) {
            // Handle error
        }
    };

    const handleSearchCars = (value: string) => {
        const newValue = value?.toUpperCase().trim();
        setLicensePlate(newValue);
        onSearchKeyword(newValue);
    };

    const onSearchCars = React.useCallback(async (value: string) => {
        try {
            await loadData(value);
        } catch (e) {
            // Handle error
        }
    }, []);

    const onSearchKeyword = React.useMemo(() => {
        return debouce(onSearchCars, 600);
    }, [onSearchCars]);

    React.useEffect(() => {
        loadData(undefined);
    }, []);

    const onSelectCar = (value: string | unknown) => {
        if (!value || typeof value !== 'string') return;
        const newValue = value.toUpperCase().trim();
        const carSelection = carList.find(car => car.licensePlate === newValue);
        if (carSelection) {
            setCarSelected(carSelection);
            selectLicensePlateForm.setFieldsValue({ cardId: newValue });
            setLicensePlate(newValue);
        }
    };

    const onSelectedTextImage = (val: any) => {
        if (!val) return;
        const newValue = val.toUpperCase().trim();
        const carSelection = carList.find(car => car.licensePlate === newValue);
        if (carSelection) {
            setCarSelected(carSelection);
            selectLicensePlateForm.setFieldsValue({
                cardId: newValue,
            });
            setLicensePlate(newValue);
        } else {
            showError('License plate not found', 'Please try again');
        }
    };

    return (
        <div className="flex flex-col w-full gap-5">
            <SectionHeader
                icon={<CarIcon />}
                title="Select Car"
                description="Select the car and add the receipts."
            />
            <AutoTrackForm
                form={selectLicensePlateForm}
                name="licensePlate"
                layout="vertical"
                style={{ width: '100%' }}
                onFinish={onSubmit}
                childNode={
                    <Form.Item
                        name="cardId"
                        label={
                            <span>
                                License plate number{' '}
                                <span className="text-[var(--system-error-500)]">
                                    *
                                </span>
                            </span>
                        }
                        rules={[
                            {
                                required: true,
                                message: 'Please enter license plate',
                            },
                        ]}
                        className="mb-0"
                    >
                        <PlateInputWrapper>
                            <UppercaseInput
                                popupClassName="certain-category-search-dropdown"
                                value={licensePlate}
                                popupMatchSelectWidth={false}
                                options={carsOptions}
                                onSearch={handleSearchCars}
                                onSelect={onSelectCar}
                                placeholder="Search license plate"
                                size="large"
                            />
                            <CameraOCR
                                embedded
                                scannerType={ScannerType.PLATE}
                                onSelectedTextImage={onSelectedTextImage}
                            />
                        </PlateInputWrapper>
                    </Form.Item>
                }
            />
            <Modal
                centered
                onCancel={() => setShowNewCar(false)}
                open={showNewCar}
                maskClosable={false}
                afterClose={() => {
                    addLicensePlateForm.resetFields();
                }}
                destroyOnClose
                title={null}
                footer={
                    <div className="grid grid-cols-[auto_auto] justify-end gap-[16px]">
                        <AutoTrackButton
                            btnType="sub"
                            btnSize="sm"
                            onClick={() => setShowNewCar(false)}
                        >
                            Cancel
                        </AutoTrackButton>
                        <AutoTrackButton
                            onClick={onSaveNewCar}
                            btnType="primary"
                            btnSize="sm"
                        >
                            Save
                        </AutoTrackButton>
                    </div>
                }
            >
                <div className="flex flex-col gap-[20px] w-full overflow-hidden h-full items-start box-border">
                    <AddLicensePlate
                        addLicensePlateForm={addLicensePlateForm}
                    />
                </div>
            </Modal>
        </div>
    );
};
